import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Icon,
  cn,
} from "@nexus/ui";
import { AI_EDIT_INSTRUCTION_MAX_LENGTH } from "@nexus/contracts";
import { useTranslation } from "react-i18next";

import { getPersonaScriptDisplayTitle } from "@/features/persona-opportunities/lib/script-display-title";

const COUNTER_VISIBLE_THRESHOLD_RATIO = 0.9;
const COUNTER_VISIBLE_FROM = AI_EDIT_INSTRUCTION_MAX_LENGTH * COUNTER_VISIBLE_THRESHOLD_RATIO;

const PROMPT_TEMPLATE_KEYS = [
  "hookAttention",
  "shorten30",
  "toneCasual",
  "strengthenCta",
  "addUrgency",
  "youngerAudience",
  "clearerDemo",
  "cutToEssentials",
] as const;

const PROMPT_TEMPLATE_DEFAULTS: Record<(typeof PROMPT_TEMPLATE_KEYS)[number], string> = {
  hookAttention: "Make the hook more attention-grabbing",
  shorten30: "Shorten to 30 seconds",
  toneCasual: "Make the tone more casual",
  strengthenCta: "Strengthen the CTA",
  addUrgency: "Add more urgency to the closing scene",
  youngerAudience: "Rewrite for a younger audience",
  clearerDemo: "Make the product demo clearer",
  cutToEssentials: "Cut the script to the essential scenes only",
};

export interface PersonaAiScriptEditorModalProps {
  open: boolean;
  onClose: () => void;
  hook: string | null;
  concept: string | null;
  opportunityTitle: string | null;
  onSubmit: (instruction: string) => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function PersonaAiScriptEditorModal({
  open,
  onClose,
  hook,
  concept,
  opportunityTitle,
  onSubmit,
  isSubmitting = false,
  error,
}: PersonaAiScriptEditorModalProps) {
  const { t } = useTranslation();
  const [instruction, setInstruction] = React.useState("");
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (open) {
      setInstruction("");
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open]);

  function handleSubmit() {
    const trimmed = instruction.trim();
    if (!trimmed || isSubmitting) return;
    onSubmit(trimmed);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  const canSubmit = instruction.trim().length > 0 && !isSubmitting;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl gap-0 p-0">
        <div className="flex items-center gap-3 border-b border-border/60 px-6 py-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--ai-fill-chip)]">
            <Icon name="sparkles" size="sm" className="text-[var(--ai-text)]" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base font-semibold leading-snug">
              {t("personaOpportunities.scripts.aiEditor.title", "AI Edit")}
            </DialogTitle>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          <DialogDescription className="text-sm text-muted-foreground">
            {t(
              "personaOpportunities.scripts.aiEditor.description",
              "Tell Adge how to rewrite this script. Scene structure stays; wording and beats change.",
            )}
          </DialogDescription>

          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
            <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground">
              {getPersonaScriptDisplayTitle(
                { hook, concept },
                t("personaOpportunities.scripts.aiEditor.untitled", "Untitled script"),
              )}
            </p>
            {opportunityTitle ? (
              <p className="mt-1 truncate text-[11px] text-muted-foreground">{opportunityTitle}</p>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {PROMPT_TEMPLATE_KEYS.map((key) => {
              const label = t(
                `personaOpportunities.scripts.aiEditor.chips.${key}`,
                PROMPT_TEMPLATE_DEFAULTS[key],
              );
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setInstruction(label);
                    textareaRef.current?.focus();
                  }}
                  disabled={isSubmitting}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs transition-colors disabled:pointer-events-none disabled:opacity-50",
                    instruction === label
                      ? "border-primary/40 bg-primary/5 text-foreground"
                      : "border-border text-muted-foreground hover:border-border/80 hover:bg-muted hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <textarea
            ref={textareaRef}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t(
              "personaOpportunities.scripts.aiEditor.placeholder",
              "e.g. Make the hook more attention-grabbing",
            )}
            rows={3}
            maxLength={AI_EDIT_INSTRUCTION_MAX_LENGTH}
            disabled={isSubmitting}
            className={cn(
              "w-full resize-none rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none transition-[color,box-shadow]",
              "placeholder:text-muted-foreground/50",
              "focus:border-[var(--ai)] focus:ring-[3px] focus:ring-[var(--ai-fill-chip)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
              error ? "border-destructive" : "border-input",
            )}
          />

          {instruction.length >= COUNTER_VISIBLE_FROM ? (
            <p
              role="status"
              aria-live="polite"
              className="-mt-2 self-end text-[11px] tabular-nums text-muted-foreground"
            >
              {t("personaOpportunities.scripts.aiEditor.characterCount", {
                current: instruction.length,
                max: AI_EDIT_INSTRUCTION_MAX_LENGTH,
                defaultValue: "{{current}} / {{max}}",
              })}
            </p>
          ) : null}

          {error ? (
            <p className="flex items-center gap-1.5 text-xs text-destructive">
              <Icon name="alert-circle" size="xs" />
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-border/60 px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {t(
              "personaOpportunities.scripts.aiEditor.pressEnterHint",
              "Press Enter to submit, Shift+Enter for a new line",
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              size="sm"
              iconStart={isSubmitting ? "loader" : "sparkles"}
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={isSubmitting ? "[&_svg]:animate-spin" : ""}
            >
              {t("personaOpportunities.scripts.aiEditor.submitSingle", "Rewrite script")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
